import { apiError } from "../../../../../../utils/formErrors";
import React, { useState } from "react";
import { Box, Stack, Select, MenuItem, CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router";
import TopBar from "../../../../../../component/TopBar";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import { WizardCard, FormSection, FieldGrid, FieldLabel, SwitchRow, WizardActions } from "../../../../../../component/wizard";
import { selectSx } from "../../../../../../component/listing";
import { createSalaryComponent, updateSalaryComponent } from "../../../../../../services/payroll";
import { getActiveLegalUnitId } from "../../../../../../services/globalData";
import { useNotify } from "../../../../../../component/NotificationProvider";

import { displayFont } from "../../../../../../theme";
const BUCKETS = ["EARNING", "DEDUCTION", "REIMBURSEMENT"];

const SalaryComponentForm = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const existing = useLocation().state?.component || null;
  const [form, setForm] = useState({
    name: existing?.name || "",
    component: existing?.component || "EARNING",
    calculationType: existing?.calculationType || "FIXED",
    value: existing?.value ?? 0,
    percentageOf: existing?.percentageOf || "CTC",
    taxable: existing?.taxable ?? true,
    isActive: existing?.isActive ?? true,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const change = (f, v) => { setForm((p) => ({ ...p, [f]: v })); setErrors((p) => ({ ...p, [f]: "" })); };

  const save = async () => {
    if (!form.name) { setErrors({ name: "Name is required" }); return; }
    setSaving(true);
    try {
      const payload = { ...form, value: Number(form.value) || 0, legalUnit: getActiveLegalUnitId() };
      const res = existing ? await updateSalaryComponent(existing._id, payload) : await createSalaryComponent(payload);
      if (res?.success === false) throw apiError(res, "Could not save component");
      notify.success(existing ? "Component updated" : "Component created");
      navigate("/app/settings/payroll/components");
    } catch (e) {
      notify.error(e?.message || "Could not save component");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Payroll"} breadcrumbs={[{ label: "Salary Components", href: "/app/settings/payroll/components" }, { label: existing ? "Edit" : "New" }]} />
      <WizardCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>{existing ? "Edit Component" : "New Salary Component"}</Box>
          <BtnSecondary to="/app/settings/payroll/components" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        </Stack>

        <FormSection title="Component">
          <FieldGrid>
            <Box>
              <FieldLabel required>Name</FieldLabel>
              <FormField type="text" value={form.name} validation={errors.name} onChange={(e) => change("name", e.target.value)} />
            </Box>
            <Box>
              <FieldLabel>Bucket</FieldLabel>
              <Select value={form.component} onChange={(e) => change("component", e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>
                {BUCKETS.map((b) => <MenuItem key={b} value={b}>{b.charAt(0) + b.slice(1).toLowerCase()}</MenuItem>)}
              </Select>
            </Box>
            <Box>
              <FieldLabel>Calculation</FieldLabel>
              <Select value={form.calculationType} onChange={(e) => change("calculationType", e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>
                <MenuItem value="FIXED">Fixed ₹ / year</MenuItem>
                <MenuItem value="PERCENTAGE">Percentage</MenuItem>
              </Select>
            </Box>
            <Box>
              <FieldLabel>{form.calculationType === "FIXED" ? "Amount (₹ / year)" : "Percent"}</FieldLabel>
              <FormField type="number" value={form.value} onChange={(e) => change("value", e.target.value)} />
            </Box>
            {form.calculationType === "PERCENTAGE" && (
              <Box>
                <FieldLabel>Percentage of</FieldLabel>
                <Select value={form.percentageOf} onChange={(e) => change("percentageOf", e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>
                  <MenuItem value="CTC">CTC</MenuItem>
                  <MenuItem value="BASIC">Basic</MenuItem>
                </Select>
              </Box>
            )}
          </FieldGrid>
          <Box sx={{ mt: "1rem" }}>
            <SwitchRow title="Taxable" description="Counts toward taxable income." checked={form.taxable} onChange={(e, c) => change("taxable", c)} />
            <SwitchRow title="Active" description="Available when building salary structures." checked={form.isActive} onChange={(e, c) => change("isActive", c)} />
          </Box>
        </FormSection>

        <WizardActions>
          <BtnSecondary to="/app/settings/payroll/components" sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>
            {saving ? <CircularProgress size={"1.25rem"} /> : existing ? "Save Changes" : "Create Component"}
          </BtnPrimary>
        </WizardActions>
      </WizardCard>
    </Box>
  );
};

export default SalaryComponentForm;
